import { useState, useCallback, useRef, useEffect } from "react";
import { useRS1Audio, RS1State, OscWaveform, LfoDest } from "./useRS1Audio";
import { Knob } from "./Knob";
import { Keyboard } from "./Keyboard";
import { PRESETS, PRESET_NAMES } from "./presets";
import { useMidi, midiNoteToName } from "../../hooks/useMidi";

const DEFAULT_STATE: RS1State = {
  ...PRESETS.init,
  volume: 0.8,
};

// Wave button icons as SVG paths
const WAVE_ICONS: Record<OscWaveform, string> = {
  sawtooth: "M2,14 L12,2 L12,14 L22,2",
  square: "M2,14 L2,2 L12,2 L12,14 L22,14 L22,2",
  sine: "M2,8 Q6,2 12,8 Q18,14 22,8",
  triangle: "M2,14 L7,2 L12,14 L17,2 L22,14",
};

export function RS1() {
  const [state, setState] = useState<RS1State>(DEFAULT_STATE);
  const [octave, setOctave] = useState(4);
  const [activeNotes, setActiveNotes] = useState<Set<string>>(new Set());
  const [presetMenuOpen, setPresetMenuOpen] = useState(false);
  const [currentPreset, setCurrentPreset] = useState("init");

  const audio = useRS1Audio(state);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const adsrCanvasRef = useRef<HTMLCanvasElement>(null);
  const filterAdsrCanvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();

  // Waveform animation
  useEffect(() => {
    const draw = () => {
      animationRef.current = requestAnimationFrame(draw);
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      const width = (canvas.width = canvas.offsetWidth * 2);
      const height = (canvas.height = canvas.offsetHeight * 2);

      ctx.fillStyle = "#0a0a0a";
      ctx.fillRect(0, 0, width, height);

      // Center line
      ctx.strokeStyle = "#1a1a1a";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(0, height / 2);
      ctx.lineTo(width, height / 2);
      ctx.stroke();

      // Get analyser data if available
      const data = audio.getAnalyserData();
      if (data) {
        ctx.strokeStyle = "#00ff88";
        ctx.lineWidth = 2;
        ctx.beginPath();

        const sliceWidth = width / data.length;
        let x = 0;

        for (let i = 0; i < data.length; i++) {
          const v = data[i] / 128.0;
          const y = (v * height) / 2;
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
          x += sliceWidth;
        }
        ctx.stroke();

        // Glow when active
        if (audio.hasActiveNotes()) {
          ctx.shadowColor = "#00ff88";
          ctx.shadowBlur = 10;
          ctx.stroke();
          ctx.shadowBlur = 0;
        }
      }
    };

    draw();
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [audio]);

  // ADSR display
  useEffect(() => {
    const drawADSR = (
      canvas: HTMLCanvasElement | null,
      a: number,
      d: number,
      s: number,
      r: number
    ) => {
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      const width = (canvas.width = canvas.offsetWidth * 2);
      const height = (canvas.height = canvas.offsetHeight * 2);

      ctx.fillStyle = "#0a0a0a";
      ctx.fillRect(0, 0, width, height);

      ctx.strokeStyle = "#00ff88";
      ctx.lineWidth = 2;
      ctx.beginPath();

      const padding = 10;
      const h = height - padding * 2;
      const w = width - padding * 2;

      const totalTime = a + d + 0.3 + r;
      const aX = padding + (a / totalTime) * w;
      const dX = aX + (d / totalTime) * w;
      const sX = dX + (0.3 / totalTime) * w;
      const rX = padding + w;

      ctx.moveTo(padding, height - padding);
      ctx.lineTo(aX, padding);
      ctx.lineTo(dX, padding + h * (1 - s));
      ctx.lineTo(sX, padding + h * (1 - s));
      ctx.lineTo(rX, height - padding);
      ctx.stroke();
    };

    drawADSR(adsrCanvasRef.current, state.attack, state.decay, state.sustain, state.release);
    drawADSR(
      filterAdsrCanvasRef.current,
      state.fAttack,
      state.fDecay,
      state.fSustain,
      state.fRelease
    );
  }, [state.attack, state.decay, state.sustain, state.release, state.fAttack, state.fDecay, state.fSustain, state.fRelease]);

  const updateParam = useCallback(
    (param: keyof RS1State, value: number) => {
      setState((s) => ({ ...s, [param]: value }));
      audio.updateParam(param, value);
    },
    [audio]
  );

  const setWaveform = useCallback(
    (osc: 1 | 2 | "lfo", waveform: OscWaveform) => {
      if (osc === "lfo") {
        setState((s) => ({ ...s, lfoWaveform: waveform }));
        audio.setLfoWaveform(waveform);
      } else {
        const key = osc === 1 ? "waveform1" : "waveform2";
        setState((s) => ({ ...s, [key]: waveform }));
        audio.setOscWaveform(osc, waveform);
      }
    },
    [audio]
  );

  const setLfoDest = useCallback((dest: LfoDest) => {
    setState((s) => ({ ...s, lfoDest: dest }));
  }, []);

  const loadPreset = useCallback(
    (name: string) => {
      const preset = PRESETS[name];
      if (!preset) return;
      setState((s) => ({ ...preset, volume: s.volume }));
      setCurrentPreset(name);
      setPresetMenuOpen(false);

      // Update audio nodes
      audio.setLfoWaveform(preset.lfoWaveform);
      audio.updateParam("lfoRate", preset.lfoRate);
      audio.updateParam("lfoDepth", preset.lfoDepth);
      audio.updateParam("drive", preset.drive);
      audio.updateParam("delayTime", preset.delayTime);
      audio.updateParam("delayMix", preset.delayMix);
      audio.updateParam("reverbMix", preset.reverbMix);
      audio.updateParam("reverbSize", preset.reverbSize);
    },
    [audio]
  );

  const handleNoteOn = useCallback(
    (note: string) => {
      setActiveNotes((s) => new Set(s).add(note));
      audio.noteOn(note, octave);
    },
    [audio, octave]
  );

  const handleNoteOff = useCallback(
    (note: string) => {
      setActiveNotes((s) => {
        const next = new Set(s);
        next.delete(note);
        return next;
      });
      audio.noteOff(note);
    },
    [audio]
  );

  // MIDI support - track active MIDI notes separately to handle octave correctly
  const midiNotesRef = useRef<Map<number, string>>(new Map());
  const [midiOctave, setMidiOctave] = useState<number | null>(null);
  const [velocitySensitivity, setVelocitySensitivity] = useState(0.7); // 0 = fixed, 1 = full velocity

  const midi = useMidi({
    onNoteOn: (midiNote, velocity) => {
      const { note, octave: noteOctave } = midiNoteToName(midiNote);
      // Create unique key for this MIDI note
      const noteKey = `${note}${noteOctave}`;
      midiNotesRef.current.set(midiNote, noteKey);
      setActiveNotes((s) => new Set(s).add(note));
      setMidiOctave(noteOctave);
      // Apply velocity sensitivity: blend between full velocity (127) and actual velocity
      const effectiveVelocity = Math.round(127 - (127 - velocity) * velocitySensitivity);
      audio.noteOn(note, noteOctave, effectiveVelocity);
    },
    onNoteOff: (midiNote) => {
      const noteKey = midiNotesRef.current.get(midiNote);
      if (noteKey) {
        const note = noteKey.replace(/\d+$/, "");
        midiNotesRef.current.delete(midiNote);
        setActiveNotes((s) => {
          const next = new Set(s);
          next.delete(note);
          return next;
        });
        audio.noteOff(note);
        // Clear MIDI octave display if no more MIDI notes held
        if (midiNotesRef.current.size === 0) {
          setMidiOctave(null);
        }
      }
    },
  });

  // Init audio on any user interaction (for MIDI to work)
  const [audioReady, setAudioReady] = useState(false);

  useEffect(() => {
    const enableAudio = () => {
      const ctx = audio.init();
      if (ctx && ctx.state !== "suspended") {
        setAudioReady(true);
      }
    };

    // Listen for any user interaction
    const events = ["click", "touchstart", "keydown"];
    events.forEach(e => document.addEventListener(e, enableAudio, { once: true }));

    return () => {
      events.forEach(e => document.removeEventListener(e, enableAudio));
    };
  }, [audio]);

  const formatPercent = (v: number) => `${Math.round(v * 100)}%`;
  const formatDetune = (v: number) => {
    const cents = Math.round((v - 0.5) * 100);
    return (cents >= 0 ? "+" : "") + cents;
  };
  const formatHz = (v: number) => `${(v * 19.9 + 0.1).toFixed(1)}Hz`;
  const formatMs = (v: number) => `${Math.round(v * 1000)}ms`;

  const WaveButton = ({
    wave,
    active,
    onClick,
  }: {
    wave: OscWaveform;
    active: boolean;
    onClick: () => void;
  }) => (
    <button
      onClick={onClick}
      className={`w-8 h-6 rounded flex items-center justify-center transition-all ${
        active ? "bg-[#00ff88]" : "bg-[#0a0a0a] border border-[#333] hover:border-[#00ff88]"
      }`}
    >
      <svg viewBox="0 0 24 16" className="w-4 h-3" stroke={active ? "#000" : "#666"} strokeWidth={2} fill="none">
        <path d={WAVE_ICONS[wave]} />
      </svg>
    </button>
  );

  return (
    <div className="bg-gradient-to-br from-[#1a1a1a] to-[#0d0d0d] rounded-2xl p-6 max-w-4xl mx-auto font-mono text-[#e0e0e0] shadow-[0_2px_0_#2a2a2a,0_4px_0_#1a1a1a,0_8px_32px_rgba(0,0,0,0.5)] relative">
      {/* Audio not ready overlay */}
      {midi.connected && !audioReady && (
        <div className="absolute inset-0 bg-black/80 rounded-2xl flex items-center justify-center z-50">
          <div className="text-center">
            <div className="text-[#ff6b35] text-lg font-bold mb-2">Click anywhere to enable audio</div>
            <div className="text-[#666] text-sm">Required for MIDI playback</div>
          </div>
        </div>
      )}
        {/* Header */}
        <div className="flex justify-between items-center mb-5 pb-3 border-b border-[#333]">
          <div>
            <div className="text-xl font-bold tracking-wider text-white">RS-1</div>
            <div className="text-[9px] text-[#666] uppercase tracking-wider">Synthesizer</div>
          </div>
          <div className="flex items-center gap-3">
            {/* MIDI indicator - always visible */}
            <div
              className={`text-[9px] uppercase tracking-wider px-2 py-1.5 rounded border ${
                midi.connected
                  ? "bg-[#00ff88] text-black font-semibold border-[#00ff88]"
                  : "bg-[#0a0a0a] text-[#444] border-[#333]"
              }`}
              title={midi.connected ? (midi.deviceName || "MIDI Connected") : "MIDI not connected"}
            >
              MIDI
            </div>
            {/* Octave display */}
            <div className="flex items-center gap-1.5 bg-[#0a0a0a] border border-[#333] rounded px-2.5 py-1.5">
              <span className="text-[9px] text-[#666] uppercase">Oct</span>
              <span className={`text-sm font-semibold min-w-[12px] text-center ${midiOctave !== null ? "text-[#ff6b35]" : "text-[#00ff88]"}`}>
                {midiOctave !== null ? midiOctave : octave}
              </span>
            </div>
            {/* Preset selector */}
            <div className="relative">
              <button
                onClick={() => setPresetMenuOpen(!presetMenuOpen)}
                className="bg-[#0a0a0a] border border-[#333] rounded px-3 py-1.5 text-[10px] text-[#888] hover:border-[#00ff88] hover:text-white flex items-center gap-1.5 min-w-[90px] justify-between"
              >
                <span>{PRESET_NAMES[currentPreset]}</span>
                <span className="text-[8px]">▼</span>
              </button>
              {presetMenuOpen && (
                <div className="absolute top-full right-0 mt-1 bg-[#1a1a1a] border border-[#333] rounded py-1 min-w-[140px] z-50">
                  {Object.keys(PRESETS).map((key) => (
                    <button
                      key={key}
                      onClick={() => loadPreset(key)}
                      className="w-full text-left px-3 py-2 text-[11px] text-[#aaa] hover:bg-[#00ff88] hover:text-black"
                    >
                      {PRESET_NAMES[key]}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

      {/* Waveform display */}
      <div className="bg-[#0a0a0a] rounded-lg h-24 mb-5 relative overflow-hidden border border-[#222]">
        <canvas ref={canvasRef} className="w-full h-full" />
        <span className="absolute bottom-2 right-3 text-[9px] text-[#444] uppercase tracking-wider">
          {activeNotes.size > 0 ? "Live" : "Waveform"}
        </span>
      </div>

      {/* Controls Row 1 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        {/* OSC 1 */}
        <div className="bg-[#151515] rounded-lg p-4 border border-[#222]">
          <div className="text-[9px] text-[#666] uppercase tracking-wider text-center mb-3">OSC 1</div>
          <div className="flex gap-1.5 justify-center">
            {(["sawtooth", "square", "sine", "triangle"] as OscWaveform[]).map((w) => (
              <WaveButton key={w} wave={w} active={state.waveform1 === w} onClick={() => setWaveform(1, w)} />
            ))}
          </div>
        </div>

        {/* OSC 2 */}
        <div className="bg-[#151515] rounded-lg p-4 border border-[#222]">
          <div className="text-[9px] text-[#666] uppercase tracking-wider text-center mb-3">OSC 2</div>
          <div className="flex gap-1.5 justify-center mb-3">
            {(["sawtooth", "square", "sine", "triangle"] as OscWaveform[]).map((w) => (
              <WaveButton key={w} wave={w} active={state.waveform2 === w} onClick={() => setWaveform(2, w)} />
            ))}
          </div>
          <div className="flex justify-center gap-4">
            <Knob
              value={state.mix}
              onChange={(v) => updateParam("mix", v)}
              label="Mix"
              displayValue={formatPercent(state.mix)}
            />
            <Knob
              value={(state.detune + 50) / 100}
              onChange={(v) => updateParam("detune", (v - 0.5) * 100)}
              label="Detune"
              displayValue={formatDetune((state.detune + 50) / 100)}
            />
          </div>
        </div>

        {/* Filter */}
        <div className="bg-[#151515] rounded-lg p-4 border border-[#222]">
          <div className="text-[9px] text-[#666] uppercase tracking-wider text-center mb-3">Filter</div>
          <div className="flex justify-center gap-4">
            <Knob
              value={state.cutoff}
              onChange={(v) => updateParam("cutoff", v)}
              label="Cutoff"
              displayValue={formatPercent(state.cutoff)}
            />
            <Knob
              value={state.resonance}
              onChange={(v) => updateParam("resonance", v)}
              label="Res"
              displayValue={formatPercent(state.resonance)}
            />
            <Knob
              value={state.filterEnvAmt}
              onChange={(v) => updateParam("filterEnvAmt", v)}
              label="Env"
              displayValue={formatPercent(state.filterEnvAmt)}
            />
          </div>
        </div>
      </div>

      {/* Controls Row 2 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        {/* Amp Envelope */}
        <div className="bg-[#151515] rounded-lg p-3 border border-[#222]">
          <div className="text-[9px] text-[#666] uppercase tracking-wider text-center mb-3">Amp Envelope</div>
          <div className="flex justify-around mb-3">
            <Knob size="sm" value={state.attack} onChange={(v) => updateParam("attack", v)} label="A" displayValue={formatPercent(state.attack)} />
            <Knob size="sm" value={state.decay} onChange={(v) => updateParam("decay", v)} label="D" displayValue={formatPercent(state.decay)} />
            <Knob size="sm" value={state.sustain} onChange={(v) => updateParam("sustain", v)} label="S" displayValue={formatPercent(state.sustain)} />
            <Knob size="sm" value={state.release} onChange={(v) => updateParam("release", v)} label="R" displayValue={formatPercent(state.release)} />
          </div>
          <div className="h-10 bg-[#0a0a0a] rounded overflow-hidden">
            <canvas ref={adsrCanvasRef} className="w-full h-full" />
          </div>
        </div>

        {/* Filter Envelope */}
        <div className="bg-[#151515] rounded-lg p-3 border border-[#222]">
          <div className="text-[9px] text-[#666] uppercase tracking-wider text-center mb-3">Filter Envelope</div>
          <div className="flex justify-around mb-3">
            <Knob size="sm" value={state.fAttack} onChange={(v) => updateParam("fAttack", v)} label="A" displayValue={formatPercent(state.fAttack)} />
            <Knob size="sm" value={state.fDecay} onChange={(v) => updateParam("fDecay", v)} label="D" displayValue={formatPercent(state.fDecay)} />
            <Knob size="sm" value={state.fSustain} onChange={(v) => updateParam("fSustain", v)} label="S" displayValue={formatPercent(state.fSustain)} />
            <Knob size="sm" value={state.fRelease} onChange={(v) => updateParam("fRelease", v)} label="R" displayValue={formatPercent(state.fRelease)} />
          </div>
          <div className="h-10 bg-[#0a0a0a] rounded overflow-hidden">
            <canvas ref={filterAdsrCanvasRef} className="w-full h-full" />
          </div>
        </div>

        {/* LFO */}
        <div className="bg-[#151515] rounded-lg p-3 border border-[#222]">
          <div className="text-[9px] text-[#666] uppercase tracking-wider text-center mb-3">LFO</div>
          <div className="flex gap-1.5 justify-center mb-2">
            {(["sine", "square", "triangle"] as OscWaveform[]).map((w) => (
              <WaveButton key={w} wave={w} active={state.lfoWaveform === w} onClick={() => setWaveform("lfo", w)} />
            ))}
          </div>
          <div className="flex gap-1 justify-center mb-2">
            {(["filter", "pitch", "amp"] as LfoDest[]).map((d) => (
              <button
                key={d}
                onClick={() => setLfoDest(d)}
                className={`px-2 py-0.5 rounded text-[8px] uppercase tracking-wide ${
                  state.lfoDest === d
                    ? "bg-[#00ff88] text-black"
                    : "bg-[#0a0a0a] border border-[#333] text-[#666] hover:border-[#00ff88]"
                }`}
              >
                {d === "filter" ? "Filt" : d}
              </button>
            ))}
          </div>
          <div className="flex justify-center gap-4">
            <Knob
              value={state.lfoRate}
              onChange={(v) => updateParam("lfoRate", v)}
              label="Rate"
              displayValue={formatHz(state.lfoRate)}
            />
            <Knob
              value={state.lfoDepth}
              onChange={(v) => updateParam("lfoDepth", v)}
              label="Depth"
              displayValue={formatPercent(state.lfoDepth)}
            />
          </div>
        </div>
      </div>

      {/* Effects */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <div className="bg-[#151515] rounded-lg p-3 border border-[#222] flex items-center gap-3">
          <span className="text-[9px] text-[#666] uppercase tracking-wider [writing-mode:vertical-rl] rotate-180">Drive</span>
          <div className="flex-1 flex justify-center">
            <Knob value={state.drive} onChange={(v) => updateParam("drive", v)} label="Amount" displayValue={formatPercent(state.drive)} />
          </div>
        </div>
        <div className="bg-[#151515] rounded-lg p-3 border border-[#222] flex items-center gap-3">
          <span className="text-[9px] text-[#666] uppercase tracking-wider [writing-mode:vertical-rl] rotate-180">Delay</span>
          <div className="flex-1 flex justify-center gap-3">
            <Knob value={state.delayTime} onChange={(v) => updateParam("delayTime", v)} label="Time" displayValue={formatMs(state.delayTime)} />
            <Knob value={state.delayMix} onChange={(v) => updateParam("delayMix", v)} label="Mix" displayValue={formatPercent(state.delayMix)} />
          </div>
        </div>
        <div className="bg-[#151515] rounded-lg p-3 border border-[#222] flex items-center gap-3">
          <span className="text-[9px] text-[#666] uppercase tracking-wider [writing-mode:vertical-rl] rotate-180">Reverb</span>
          <div className="flex-1 flex justify-center gap-3">
            <Knob value={state.reverbSize} onChange={(v) => updateParam("reverbSize", v)} label="Size" displayValue={formatPercent(state.reverbSize)} />
            <Knob value={state.reverbMix} onChange={(v) => updateParam("reverbMix", v)} label="Mix" displayValue={formatPercent(state.reverbMix)} />
          </div>
        </div>
      </div>

      {/* Keyboard */}
      <Keyboard
        onNoteOn={handleNoteOn}
        onNoteOff={handleNoteOff}
        octave={octave}
        onOctaveChange={setOctave}
        activeNotes={activeNotes}
        isActive={true}
      />

      {/* Footer */}
      <div className="flex justify-between items-center mt-4 text-[9px] text-[#444]">
        <span className="hidden md:inline">Keys: A-L,;,' (white) · W,E,T,Y,U,O,P (black) · Z/X (octave)</span>
        <span className="md:hidden">Tap keys to play</span>
        <div className="flex items-center gap-4">
          {midi.connected && (
            <div className="flex items-center gap-2">
              <span className="text-[#ff6b35]">Vel</span>
              <Knob
                size="sm"
                value={velocitySensitivity}
                onChange={setVelocitySensitivity}
                label=""
                displayValue={formatPercent(velocitySensitivity)}
              />
            </div>
          )}
          <div className="flex items-center gap-2">
            <span>Vol</span>
            <Knob
              size="sm"
              value={state.volume}
              onChange={(v) => updateParam("volume", v)}
              label=""
              displayValue={formatPercent(state.volume)}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
